import React, { useEffect, useState } from 'react';
import { Box, Typography, Stack, useTheme } from '@mui/material';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';

interface ActivePromoResponse {
  code: string;
  discount_percent: number;
  expires_at: string;
}

interface TimeLeft {
  totalMs: number;
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

const computeTimeLeft = (expiresAt: string): TimeLeft => {
  const now = new Date().getTime();
  const end = new Date(expiresAt).getTime();
  const totalMs = Math.max(end - now, 0);

  const days = Math.floor(totalMs / (1000 * 60 * 60 * 24));
  const hours = Math.floor((totalMs / (1000 * 60 * 60)) % 24);
  const minutes = Math.floor((totalMs / (1000 * 60)) % 60);
  const seconds = Math.floor((totalMs / 1000) % 60);

  return { totalMs, days, hours, minutes, seconds };
};

const formatCountdown = (time: TimeLeft): string => {
  const { totalMs, days, hours, minutes, seconds } = time;

  if (totalMs <= 0) {
    return 'Offre expirée';
  }

  const totalHours = Math.floor(totalMs / (1000 * 60 * 60));

  if (days >= 1) {
    return `${days} j ${hours} h`;
  }

  if (totalHours >= 1) {
    return `${totalHours} h ${minutes} min`;
  }

  if (minutes >= 1) {
    return `${minutes} min ${seconds.toString().padStart(2, '0')} s`;
  }

  return `${seconds} s`;
};

const PromoCountdownBanner: React.FC = () => {
  const theme = useTheme();
  const [promo, setPromo] = useState<ActivePromoResponse | null>(null);
  const [timeLeft, setTimeLeft] = useState<TimeLeft | null>(null);
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    let isMounted = true;

    const fetchPromo = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/promo-codes/active-banner`);

        if (res.status === 204) {
          if (isMounted) {
            setPromo(null);
            setTimeLeft(null);
          }
          return;
        }

        if (!res.ok) {
          return;
        }

        const data = (await res.json()) as ActivePromoResponse;
        if (isMounted && data && data.code && data.expires_at) {
          setPromo(data);
          setTimeLeft(computeTimeLeft(data.expires_at));
        }
      } catch {
        // On ne casse pas l'UI si le bandeau échoue à charger
      }
    };

    fetchPromo();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const y = window.scrollY || window.pageYOffset || 0;
      const shouldBeVisible = y < 120;
      setIsVisible((prev) => (prev === shouldBeVisible ? prev : shouldBeVisible));
    };

    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (!promo || !promo.expires_at) return;

    const update = () => {
      setTimeLeft(computeTimeLeft(promo.expires_at));
    };

    update();
    const interval = setInterval(update, 1000);
    return () => clearInterval(interval);
  }, [promo]);

  if (!promo || !timeLeft || timeLeft.totalMs <= 0) {
    return null;
  }

  return (
    <Box
      sx={{
        position: 'fixed',
        top: { xs: 64, md: 72 },
        left: 0,
        right: 0,
        zIndex: 1300,
        px: { xs: 1.5, sm: 3 },
        transform: isVisible ? 'translateY(0)' : 'translateY(-130%)',
        opacity: isVisible ? 1 : 0,
        transition: 'transform 0.4s ease, opacity 0.4s ease',
        pointerEvents: isVisible ? 'auto' : 'none',
      }}
    >
      <Box
        sx={{
          maxWidth: 1200,
          mx: 'auto',
          borderRadius: 999,
          bgcolor: theme.palette.secondary.main,
          color: theme.palette.secondary.contrastText,
          boxShadow: 6,
          px: { xs: 2, sm: 3 },
          py: { xs: 1, sm: 1.2 },
        }}
      >
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1.5}
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          justifyContent="space-between"
        >
          <Box>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: 1.2,
              }}
            >
              Offre limitée dans le temps
            </Typography>
            <Typography variant="body2">
              Utilisez le code <strong>{promo.code}</strong> et profitez de{' '}
              <strong>-{promo.discount_percent}%</strong> sur votre commande avant la fin du
              compte à rebours.
            </Typography>
          </Box>

          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            sx={{
              mt: { xs: 0.5, sm: 0 },
            }}
          >
            <Box
              sx={{
                bgcolor: 'rgba(0,0,0,0.18)',
                borderRadius: 999,
                px: 2,
                py: 0.5,
                minWidth: 120,
                textAlign: 'center',
              }}
            >
              <Typography variant="caption" sx={{ opacity: 0.9 }}>
                Temps restant
              </Typography>
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 700, letterSpacing: 1 }}
              >
                {formatCountdown(timeLeft)}
              </Typography>
            </Box>
          </Stack>
        </Stack>
      </Box>
    </Box>
  );
};

export default PromoCountdownBanner;
